// --- React
import React, {useEffect} from 'react';
// --- Hooks
import {useDialogsActions} from '../hooks';
import {useDialogsForm} from '../forms';
// --- UI Components
import Typography from '@dtci/spellbook/Typography';
import {InputLabel} from '../../InputLabel/InputLabel';
import Input from '@dtci/spellbook/Input';

/**
 * TagsForm Component
 *
 * This component renders a form for creating or editing tags.
 * It includes fields for tag name and displays additional information when editing.
 *
 * @component
 * @param {Object} data - The data object containing tag information.
 * @param action - The action type, either 'create' or 'edit'.
 * @returns {JSX.Element} The rendered TagsForm component.
 */
const TagsForm = ({ data, action }) => {
    const form = useDialogsForm();

    useEffect(() => {
        if (
            (action === 'edit' || action === 'update') &&
            data?.tagName &&
            form.state.formData.tagName !== data.tagName
        ) {
            form.setValue('tagName', data.tagName);
        }
        if ((action === 'create' || action === 'add') && !form.state.formData.tagName) {
            form.setValue('tagName', '');
        }
    }, [data?.tagName, action]);

    const handleOnchange = (name) => {
        form.setValue('tagName', name);
    };

    return (
        <div className="flexBox">
            {action === 'create' || action === 'add' ? (
                <InputLabel label="Tag Name">
                    <Input
                        labelText="tag name"
                        onChange={(e) => handleOnchange(e.target.value)}
                        width="large"
                        value={form.state.formData.tagName || ''}
                    />
                </InputLabel>
            ) : (
                <>
                    <InputLabel label="ID">
                        <Typography variant="T20">{data?.id}</Typography>
                    </InputLabel>
                    <InputLabel label="Tag Name">
                        <Input
                            labelText="tag name"
                            onChange={(e) => handleOnchange(e.target.value)}
                            width="large"
                            value={form.state.formData.tagName || ''}
                        />
                    </InputLabel>
                    <InputLabel label="Created At">
                        <Typography variant="T20">{data?.createdAt}</Typography>
                    </InputLabel>
                    <InputLabel label="Created By">
                        <Typography variant="T20">{data?.createdBy?.username}</Typography>
                    </InputLabel>
                    <InputLabel label="Modified At">
                        <Typography variant="T20">{data?.modifiedAt}</Typography>
                    </InputLabel>
                    <InputLabel label="Modified By">
                        <Typography variant="T20">{data?.modifiedBy?.username}</Typography>
                    </InputLabel>
                </>
            )}
        </div>
    );
};

/**
 * useTagsDialog Hook
 *
 * This hook provides functionality to open a tags creation/editing dialog.
 * It uses the useDialogsActions and useDialogsForm hooks to manage the dialog state.
 * It also provides a handleSubmit function to handle the form submission.
 *
 * @returns {Object} An object containing the openTagsDialog function.
 */
export const useTagsDialog = () => {
    const {openDialog, closeDialog} = useDialogsActions();
    const form = useDialogsForm();

    // Function to handle form submission
    const handleSubmit = (onSubmit, action) => () => {
        onSubmit(form.state.formData);
        form.setValues({ tagName: '' });
        closeDialog();
    };

    // Function to open the tags dialog
    const openTagsDialog = (data, action, onConfirm) =>
        openDialog({
            title: action === 'create' || action === 'add' ? 'Create New Tag' : 'Edit Tag',
            content: <TagsForm data={data} action={action}/>,
            closeAction: true,
            primaryAction: {
                label: 'Save',
                action: handleSubmit(onConfirm, action),
            },
        });

    return {openTagsDialog};
};
